import { readStored } from '@/lib/storage'
import { scheduleSearchEvent } from '@/lib/events'
import { httpJson, NETWORK_TIMEOUT_MS, withTimeout } from '@/lib/compat'
import { readLanguage, withLanguage } from '@/lib/language'

/**
 * Public kataloq API-si (`/api/catalog/*`, hamısı anonimdir).
 *
 * ⚠ Tiplər əl ilə yazılıb — `@kinobox/api-client` codegen-i TV-də İŞLƏDİLMİR.
 * Səbəb: generasiya olunan client `fetch` + ESM + modern runtime API-lar
 * tələb edir və bu app məhz onlardan qaçır (PERFORMANCE.md). Sahələr
 * backend-dəki `PublicHomeDto`/`PublicContentDto` ilə üzbəüzdür; dəyişəndə
 * bu fayl də yenilənməlidir.
 *
 * ⚠ HƏR SORĞUNUN TIMEOUT-U VAR. TV-də şəbəkə tez-tez «asılı» qalır (Wi-Fi
 * yuxu rejimi) və cavabsız `fetch` app-ı «Yüklənir…» ekranında dondurur —
 * istifadəçi bunu donma sayır. Timeout dolan kimi mock kataloqa düşürük ki,
 * ekranda HƏMİŞƏ nəsə olsun (qara ekran qadağandır — CLAUDE.md §6).
 */

const TIMEOUT_MS = 6000

/*
  2026-10-23: KLİKDƏN SONRAKI SORĞULARIN HƏDDİ ÜMUMİ TRANSPORT HƏDDİDİR (20 s).
    NƏ TAPILDI: detal (`content/{slug}`) və «Tezliklə» kartı (`upcoming/{slug}`) 6 saniyəlik
    `withTimeout` ilə gedirdi, halbuki `compat.ts` → `NETWORK_TIMEOUT_MS` məhz «TV-də ilk sorğu
    10 saniyəni keçir» ölçməsi ilə 20 saniyəyə qaldırılıb. XHR arxada davam edirdi, cavab isə
    atılırdı.
    ƏVVƏL: TV brauzerində film modalı «açıla bilmədi» yazırdı, «İndi izlə» isə ƏBƏDİ sönük
    qalırdı (fokus «Bağla»da) — «Pərdə» və başqa filmlərin «açılmır» şikayəti.
    NİYƏ BELƏ: istifadəçi bu iki sorğunu GÖZLƏYİR (klikdən sonra), boot sorğuları kimi
    oflayn vəziyyətə düşmək mənası yoxdur. Ana ekran sorğuları 6 saniyədə qalır.
*/
const CLICK_TIMEOUT_MS = NETWORK_TIMEOUT_MS

/** Kataloq kartının TV üçün minimal forması — sıra çəkilməsi üçün lazım olan sahələr. */
export interface ContentCard {
  id: string
  slug: string
  title: string
  kind: string
  ageLimit: number | null
  year: number | null
  posterUrl: string | null
  rating: number | null
  /**
   * ── Fokus qatının meta sətirləri (`ui/card.ts` → `.card__hover`) ──────────
   *
   * ⚠ ÜÇÜ DƏ KARTIN ÖZ CAVABINDA GƏLİR — DETAL SORĞUSU ATILMIR. Server bu
   *   sahələri 2026-08-07-dən `PublicContentCardDto`-ya qoyub (backend qeydi:
   *   sayt hər hover-də ayrıca `GET /api/catalog/content/{slug}` atırdı).
   *   TV-də həmin yol ÜMUMİYYƏTLƏ mümkün deyil: fokus saniyədə ~11 kart dəyişir
   *   (PERFORMANCE.md §2) — hər addımda sorğu atmaq şəbəkəni və əsas axını
   *   boğardı, üstəlik app sıfır-reaktivdir, yəni sonradan gələn cavabı yazmaq
   *   üçün DOM-a təkrar toxunmaq lazım olardı.
   *
   * ⚠ OPSİONALDIR (`?`): köhnə API binarı bu sahələri vermir və oflayn mock
   *   kataloqda da yoxdur — həmin halda sətir sadəcə qısalır, kart qurulur.
   * ⚠ `durationMinutes` SERİALDA `null` olur (`contents.duration_minutes` yalnız
   *   tam metrajlıdadır) — bu, nasazlıq deyil, gözlənilən haldır.
   * ⚠ Janr/əhval ADLARIDIR, slug deyil: server onları cari dildə qaytarır
   *   (dil → default dil → hər hansı dil), client slug-dan adı qura bilməzdi.
   */
  durationMinutes?: number | null
  genreNames?: string[] | null
  moodNames?: string[] | null
  /**
   * 16:9 kadr — sonluq ekranının xanaları (`ui/watchOutro.ts`) bunu işlədir.
   *
   * ⚠ NİYƏ AFİŞA YETMİR: son ekranda bütün xanalar EYNİ nisbətdə olmalıdır
   *   (sahib şikayəti 2026-08-21 — qarışıq nisbət «dartılmış boş sütunlar»
   *   görüntüsü verirdi). Boşdursa xana afişaya düşür, yəni köhnə API və oflayn
   *   mock ilə də kart qurulur.
   */
  backdropUrl?: string | null
  /**
   * Redaksiya filmi «Tezliklə» bölməsinə salıb (`cnt.contents.is_coming_soon`).
   *
   * ⚠ NİYƏ KARTDA (MYTasks 709, sahib: «tezliklə bölümündə olan film homepedən
   *   açanda da eyni açsın»): eyni film həm kolleksiya sırasında, həm də
   *   «Tezliklə» ekranında olur (canlıda: `akinak`, `cennetin-i-qametgahi`).
   *   Bayraqsız halda ana ekrandan ADİ modal, «Tezliklə»dən isə treyler modalı
   *   açılırdı — bir film, iki ayrı ekran.
   * ⚠ Modalın MƏLUMATI bundan gəlmir: `openDetailModal` bayrağı görəndə
   *   `getUpcomingItem(slug)` çağırır (aşağıda).
   * ⚠ Opsional: köhnə API binarı sahəni vermir — o halda köhnə davranış qalır.
   */
  isComingSoon?: boolean
}

/** Ana ekranın hero slaydı. */
export interface HeroItem extends ContentCard {
  tagline: string | null
  backdropUrl: string | null
  trailerUrl: string | null
  /**
   * Janr ADLARI — hero meta sətri üçün (sahib maketi 2026-08-11: «8.3 · 2021 ·
   * triller, komediya, dram · 1 saat · 16+»).
   *
   * ⚠ ADI `genres`-dir, kartdakı `genreNames` DEYİL: server hero cavabında
   *   sahəni məhz belə adlandırır (`PublicHeroDto.Genres`). Ad uyğunlaşdırmaq
   *   üçün ayrıca map yazılmır — `normalizeHome` hero-nu olduğu kimi ötürür.
   * ⚠ Opsional: köhnə API binarı bu sahəni vermir, o halda meta sətri sadəcə
   *   qısalır.
   */
  genres?: string[] | null
  /**
   * Tam sinopsis — maketdə başlığın altındakı mətn bloku.
   *
   * ⚠ TAQLAYNDAN AYRIDIR: `tagline` tək cümləlik şüardır («Onlar sizi izləyir !»)
   *   və kontentlərin çoxunda boşdur; maketdə isə bir neçə sətirlik təsvir var.
   *   Ekranda əvvəl bu göstərilir, yoxdursa taqlayna düşülür.
   */
  description?: string | null
}

/** Ana ekranın bir sırası — başlıq + kartlar. */
export interface HomeBlock {
  showRank?: boolean
  wideItems?: boolean
  id: string
  slug: string
  title: string
  items: ContentCard[]
}

/** Ana ekranın tam cavabı. */
export interface Home {
  heroes: HeroItem[]
  blocks: HomeBlock[]
  /** Data mənbəyi: `api` — serverdən; `empty` — cavab alınmadı və ya kataloq boşdur. */
  source: 'api' | 'empty'
}

/** Cavab alınmayanda və ya kataloq boş olanda ana ekranın vəziyyəti — uydurma məzmun YOXDUR (28.3). */
const EMPTY_HOME: Home = { heroes: [], blocks: [], source: 'empty' }

/** Detal ekranındakı heyət sətri. */
export interface CastMember {
  personId: string
  slug: string
  fullName: string
  characterName: string | null
  roleType: string | null
}

/**
 * Serialın bir bölümü (`cnt.episodes`).
 *
 * ⚠ `number` MÖVSÜM DAXİLİNDƏ nömrədir və BOŞLUQLU ola bilər (köhnə kataloqda
 *   bəzi buraxılışlar miqrasiya olunmayıb) — «növbəti bölüm» HEÇ VAXT `number+1`
 *   ilə axtarılmır, siyahıdakı NÖVBƏTİ elementdir (`nextEpisode`).
 */
export interface Episode {
  id: string
  seasonNumber: number
  number: number
  title: string | null
  description: string | null
  durationMinutes: number | null
  thumbnailUrl: string | null
  hasVideo: boolean
}

/** Film/serial detal ekranının məlumatı. */
export interface ContentDetail {
  id: string
  slug: string
  title: string
  kind: string
  year: number | null
  ageLimit: number | null
  durationMinutes: number | null
  tagline: string | null
  description: string | null
  posterUrl: string | null
  backdropUrl: string | null
  trailerUrl: string | null
  rating: number | null
  genres: string[]
  cast: CastMember[]
  similar: ContentCard[]
  /** YALNIZ serialda dolur (backend filmdə sorğu belə atmır). */
  episodes: Episode[]
  /**
   * Redaksiya «Tezliklə» bölməsinə salıb — oynatma BAĞLIDIR (MYTasks 499).
   *
   * ⚠ Təhlükəsizlik sərhədi DEYİL: həqiqi qapı playback biletindədir
   *   (`Playback.ComingSoon`). Bu sahə yalnız «İzlə» yerinə «Tezliklə» yazmaq
   *   üçündür — köhnə cavabda yoxdursa `undefined` gəlir və film adi filmdir.
   */
  isComingSoon?: boolean
  /**
   * Sonluq qatı (dəyərləndirmə + oxşarlar) filmin bitməsinə neçə saniyə qalmış
   * açılsın — `cnt.contents.outro_seconds` (V064).
   *
   * ⚠ OPSİONALDIR: redaksiya doldurmayıbsa `null` gəlir və oynadıcı öz defoltuna
   *   düşür (30 saniyə — `screens/player.ts → OUTRO_DEFAULT_SECONDS`). Titrlərin
   *   uzunluğu filmdən filmə kəskin dəyişdiyi üçün sütun filmə xas dəqiqləşdirmədir.
   */
  outroSeconds?: number | null
}

/**
 * Verilmiş bölümdən SONRAKI oynana bilən bölüm (avtomatik keçid üçün);
 * sonuncuda `null`. Videosuz bölümlər atlanır — əks halda avtomatik keçid
 * dərhal «hazır deyil» ekranına düşərdi.
 */
export function nextEpisode(episodes: Episode[], currentId: string): Episode | null {
  let index = -1
  for (let i = 0; i < episodes.length; i++) {
    if ((episodes[i] as Episode).id === currentId) {
      index = i
      break
    }
  }
  if (index < 0) return null
  for (let i = index + 1; i < episodes.length; i++) {
    const candidate = episodes[i] as Episode
    if (candidate.hasVideo) return candidate
  }
  return null
}

/**
 * Verilmiş bölümdən ƏVVƏLKİ oynana bilən bölüm (pultun «əvvəlki trek» düyməsi və
 * ⚙ panelindəki bölüm siyahısı üçün — MYTasks 547).
 *
 * ⚠ `nextEpisode`-un güzgüsüdür və eyni səbəbdən NÖMRƏYƏ görə deyil, SİYAHIYA
 *   görə axtarır: köhnə kataloqda bölüm nömrələri boşluqludur (2, 6, 7, 16…),
 *   ona görə «nömrə − 1» çox vaxt mövcud olmayan bölümdür.
 */
export function previousEpisode(episodes: Episode[], currentId: string): Episode | null {
  let index = -1
  for (let i = 0; i < episodes.length; i++) {
    if ((episodes[i] as Episode).id === currentId) {
      index = i
      break
    }
  }
  if (index < 0) return null
  for (let i = index - 1; i >= 0; i--) {
    const candidate = episodes[i] as Episode
    if (candidate.hasVideo) return candidate
  }
  return null
}

/** Oynana bilən ilk bölüm — serialda «İzlə» buradan başlayır. */
export function firstPlayableEpisode(episodes: Episode[]): Episode | null {
  for (let i = 0; i < episodes.length; i++) {
    const candidate = episodes[i] as Episode
    if (candidate.hasVideo) return candidate
  }
  return null
}

/** Bir sırada saxlanılan maksimum kart — DOM həcmini məhdudlaşdırır. */
const MAX_ITEMS_PER_ROW = 14

/** Ekranda göstərilən maksimum sıra. */
const MAX_ROWS = 12

/**
 * Yaddaşda saxlanılan hero sayı.
 *
 * ⚠ SERVER ARTIQ ÖZÜ QARIŞDIRIR (müqavilə 1.3, 2026-08-01): `heroes` 6 deyil,
 * **17-yə qədər** element qaytarır və HƏR SORĞUDA qarışdırılmış gəlir
 * (`Cache-Control: no-store`). Ona görə burada və ekranda ƏLAVƏ SHUFFLE YOXDUR
 * — client tərəfdə qarışdırmaq həm artıq işdir, həm də «hansı hero göstərildi»
 * sualını iki yerə bölür.
 *
 * TV ekranı hero-nun YALNIZ BİRİNCİSİNİ göstərir (`home.ts` → `home.heroes[0]`),
 * avtomatik dövr edən karusel və nöqtə/indikator cərgəsi YOXDUR
 * (CLAUDE.md §3 qırmızı xətt 4 — «auto-rotate hero yoxdur»). Yəni 17 element
 * daşma riski yaratmır; hovuzun böyüməsi sadəcə hər açılışda başqa filmin
 * çıxması deməkdir. Qalan sətirlər ehtiyat kimi saxlanılır (ilk element boş
 * posterlə gəlsə əl ilə seçim üçün) — 170 kartlıq ekranda 6 obyektin yaddaş
 * dəyəri sıfıra yaxındır.
 */
const MAX_HEROES = 6

/**
 * ⚠ `?platform=tv` MƏCBURİDİR (API müqaviləsi 2026-08-01). Parametr verilməsə
 * server yalnız «hamısı» (platform=0) bloklarını qaytarır — yəni TV-yə xas
 * bloklar (gələcəkdə) heç vaxt görünməz. Veb-ə xas nömrələnən «Gündəmdə olanlar»
 * sırası isə `platform=1`-dir və TV cavabına QƏSDƏN düşmür.
 */
// 2026-10-22: surface=webtv blok kartlarına /upload/backdrop/* hero kompozisiyasını
// qaytarırdı: kiçik afişa sağda, qalan hissə qara. Ona görə BLOKLAR surface=any ilə gəlir.
const HOME_PATH = '/api/catalog/home?platform=tv&surface=any'
/*
  2026-10-23: hero-lar AYRICA, TV şəkil zənciri ilə (compat.ts surface=webtv qoşur).
    NƏ ÖLÇÜLDÜ (canlı api.kinobox.az): home?platform=tv&surface=any cavabında 15 hero-nun
    14-ünün backdropUrl-i boş idi → hero afişaya düşürdü; surface=webtv və /heroes cavabında
    15/15 geniş şəkil var, film dəsti və sahələr home ilə eynidir.
    ƏVVƏL: 2026-10-22 düzəlişi bütün home-u Any-yə keçirdi və hero da TV şəkillərini itirdi
    (sayt Any işlətmir — onun zənciri web → tv → any-dir, IClientSurface.cs).
    NİYƏ BELƏ: kart düzəlişi qalır, hero-nun TV şəkli qayıdır; /heroes kiçik cavabdır və
    home ilə paralel gedir. Alınmasa home-un öz hero-ları qalır.
*/
const HEROES_PATH = '/api/catalog/heroes'

/** Ana ekranın məlumatını çəkir (TV platforması üçün süzülmüş bloklar). */
export function getHome(): Promise<Home> {
  /*
    ⚠ UYDURMA KATALOQ YOXDUR (2026-09-25, BusinessRequirement 28.3): əvvəl taymaut,
      xəta və BOŞ cavabda quraşdırılmış saxta kataloq (`data/mock.ts`) göstərilirdi —
      ana ekranda «OFLAYN KATALOQ» nişanı ilə, detal və axtarışda isə nişansız; saxta
      filmlərin bəziləri 16+ idi və uşaq profilinə də düşürdü. İndi uğursuzluq BOŞ
      ana ekran verir və `screens/home.ts` izahlı «Yenidən cəhd et» vəziyyətini
      göstərir (qara ekran yox, qırmızı xətt 6).
  */
  const tvHeroes = withTimeout(httpJson<unknown>(withLanguage(HEROES_PATH)), TIMEOUT_MS, 'catalog/heroes')
    .then((raw) => (Array.isArray(raw) ? (raw as HeroItem[]) : null))
    .catch(() => null)
  return withTimeout(httpJson<unknown>(withLanguage(HOME_PATH)), TIMEOUT_MS, 'catalog/home')
    .then((raw) => normalizeHome(raw))
    .catch(() => EMPTY_HOME)
    .then((home) => tvHeroes.then((heroes): Home =>
      home.source === 'api' && heroes && heroes.length > 0
        ? { heroes: heroes.slice(0, MAX_HEROES), blocks: home.blocks, source: home.source }
        : home))
}

/** Bir kontenti slug üzrə çəkir. Uğursuzluqda `null` — səbəbi lazımdırsa `lookupContent`. */
export function getContent(slug: string): Promise<ContentDetail | null> {
  return lookupContent(slug).then((result) => result.detail)
}

/** Detal sorğusunun nəticəsi: `notFound` yalnız server 404 verəndə `true`-dur. */
export interface ContentLookup {
  detail: ContentDetail | null
  notFound: boolean
}

/** `httpJson` xətası `… → HTTP 404` formasındadır (`lib/compat.ts`). */
function isHttpNotFound(error: unknown): boolean {
  return error instanceof Error && error.message.endsWith(' → HTTP 404')
}

/**
 * `getContent`-in səbəbli variantı.
 *
 * ⚠ NİYƏ VAR (BR 9.1, audit 2026-10-02): `getContent` hər uğursuzluqda `null`
 *   verir və brauzer detal ekranı 404-ü də «Film açıla bilmədi. Bir azdan yenidən
 *   yoxlayın» kimi göstərirdi. Halbuki uşaq profilində bağlı film, söndürülmüş və
 *   ya silinmiş film serverdə 404-dür — «yenidən yoxlayın» istifadəçini boş yerə
 *   gözlədir. Qayda: «paylaşılan link uşaq profilində tapılmadı verir». Şəbəkə
 *   xətası/taymaut isə «açıla bilmədi» olaraq qalır.
 */
export function lookupContent(slug: string): Promise<ContentLookup> {
  return withTimeout(
    httpJson<ContentDetail>(withLanguage('/api/catalog/content/' + encodeURIComponent(slug))),
    CLICK_TIMEOUT_MS,
    'catalog/content',
  )
    .then((detail) => {
      // ⚠ Köhnə API binarında `episodes` YOXDUR — massiv olmayan dəyər ekranı
      //   çökdürərdi (`for` dövrü `undefined.length` oxuyar). Mock detal da bu
      //   sahəni vermir, ona görə normallaşdırma burada, bir yerdədir.
      if (detail && !detail.episodes) detail.episodes = []
      /**
       * ⚠⚠ HEYƏT SƏTRİ HEÇ VAXT GÖRÜNMÜRDÜ (2026-08-11-də brauzer variantını
       *    qurarkən tapıldı). Server `PublicPersonCreditDto`-da adı **`name`**
       *    sahəsində göndərir, bu interfeys isə `fullName` gözləyirdi. Nəticə
       *    SƏSSİZ idi, çünki `screens/detail.ts` `if (member.fullName)` ilə
       *    yoxlayır — massiv dolu, hər ad `undefined`, siyahı boş, «Rollarda: …»
       *    sətri ÜMUMİYYƏTLƏ çəkilmirdi. Xəta konsola düşmür, boş yer də
       *    görünmür; ona görə uzun müddət fərq edilməyib.
       * ⚠ NORMALLAŞDIRMA BURADADIR, ekranda YOX: iki səth (cihaz + brauzer) eyni
       *   sahəni oxuyur; ekranda düzəltsəydik biri düzələn kimi o biri arxada
       *   qalardı (`compat.ts`-dəki fetch/XHR dərsi).
       */
      if (detail && detail.cast) {
        for (let i = 0; i < detail.cast.length; i++) {
          const member = detail.cast[i] as CastMember & { name?: string }
          if (member && !member.fullName && member.name) member.fullName = member.name
        }
      }
      return { detail: detail || null, notFound: false }
    })
    // Uydurma detal yoxdur (28.3) — çağıran tərəf `null`-u «açıla bilmədi» və ya
    // (404-də) «tapılmadı» kimi göstərir.
    .catch((error: unknown) => ({ detail: null, notFound: isHttpNotFound(error) }))
}

/**
 * «Tezliklə» kartı (`GET /api/catalog/upcoming` → `PublicUpcomingCardDto`).
 *
 * ⚠ MODALIN BÜTÜN MƏLUMATI BU CAVABDADIR (523/528 müqaviləsi): kontent HƏLƏ
 *   DƏRC OLUNMAYIB, detal ucu (`content/{slug}`) yalnız status=3 qaytarır və
 *   plana salınmış kontentdə 404 verir — yəni klikdən sonra ƏLAVƏ SORĞU YOXDUR.
 *   Server modal sahələrini (təsvir · treyler · başlıq kadrı · müddət ·
 *   janr/əhval) 2026-08-07-dən kartın özünə qoyub.
 * ⚠ REYTİNQ QƏSDƏN YOXDUR — film çıxmayıb, qiymətləndirmə də yoxdur; kartda
 *   nişan, modalda ★ sətri yazılmır (normallaşdırma `rating`-i məcburi `null`
 *   edir ki, `ui/card.ts`-in ümumi qurucusu nişan çəkməsin).
 */
export interface UpcomingItem extends ContentCard {
  /** Planlı yayım vaxtı (status=Scheduled) — premyera sətrində üstündür. */
  publishAt: string | null
  /** Premyera tarixi (dərc olunub, amma tarixi gələcəkdədir). */
  releaseDate: string | null
  description: string | null
  /** Treyler mənbəyi (`mux:`/`vimeo:` prefiksli) — `lib/trailer.ts` formatı. */
  trailerUrl: string | null
  backdropUrl: string | null
  /**
   * HEYƏT — rejissor və rollarda, AYRI massivlərdə (MYTasks 709, sahib: «film
   * haqqında tam məlumat yaz heyət rejissor və s. da olsun»).
   *
   * ⚠ ADLARDIR, KEÇİD DEYİL: modalda mətn kimi yazılır — dərc olunmamış filmdən
   *   şəxs səhifəsinə keçid modalın «bir pəncərədə oxu» mənasını pozardı.
   * ⚠ Opsional: köhnə API binarı bu sahələri vermir, o halda sətir çəkilmir.
   */
  directorNames?: string[] | null
  castNames?: string[] | null
}

/**
 * «Tezliklə» siyahısı.
 *
 * ⚠ XƏTADA `null` (boş siyahı DEYİL) — `browseCatalog` ilə eyni qərar: ekran
 *   «premyera yoxdur» ilə «yüklənmədi» hallarını ayırd etməlidir. Mock ehtiyat
 *   YOXDUR: uydurma premyera tarixi göstərmək boş ekrandan pisdir.
 */
/**
 * «Tezliklə» bölməsində film varmı — brauzer menyusunda bəndin görünmə şərti.
 *
 * ⚠ SAHİB TƏLƏBİ (2026-10-02, MYTasks): «əgər tezliklə bölümündə film yoxdursa menuda da
 *   görünməsin, varsa menuda da görünsün». Saytda eyni qayda `lib/upcoming.server.ts`-dədir.
 * ⚠ Sorğu ALINMASA bənd GÖRÜNÜR (köhnə davranış) — səhvən «film yoxdur» deməyək.
 */
let upcomingAvailable = true

export function isUpcomingAvailable(): boolean {
  return upcomingAvailable
}

/** Boot-da bir dəfə — heç vaxt reject etmir. */
export function loadUpcomingAvailability(): Promise<void> {
  return getUpcoming().then((items) => {
    if (items) upcomingAvailable = items.length > 0
  })
}

export function getUpcoming(): Promise<UpcomingItem[] | null> {
  return withTimeout(
    httpJson<UpcomingItem[]>(withLanguage('/api/catalog/upcoming')),
    TIMEOUT_MS,
    'catalog/upcoming',
  )
    .then((raw) => {
      if (!raw || !raw.length) return []
      const items: UpcomingItem[] = []
      for (let i = 0; i < raw.length; i++) {
        const item = raw[i]
        if (!item || !item.slug) continue
        // Başlıq tərcüməsi hələ yazılmayıbsa slug göstərilir (Android VM qaydası).
        if (!item.title) item.title = item.slug
        // ⚠ 0 = məhdudiyyət yoxdur → «0+» nişanı yazılmasın (Android `takeIf > 0`).
        if (!item.ageLimit) item.ageLimit = null
        // Reytinq MƏCBURİ atılır (interfeys şərhinə bax) — server göndərsə belə.
        item.rating = null
        item.year = item.year || null
        item.posterUrl = item.posterUrl || null
        item.publishAt = item.publishAt || null
        item.releaseDate = item.releaseDate || null
        item.description = item.description || null
        item.trailerUrl = item.trailerUrl || null
        item.backdropUrl = item.backdropUrl || null
        items.push(item)
      }
      return items
    })
    .catch(() => null)
}

/**
 * BİR «Tezliklə» kartı — slug üzrə (MYTasks 709).
 *
 * Ana ekrandan (kolleksiya sırasından) açılan plana salınmış film eyni treyler
 * modalını göstərsin deyə: kartda yalnız `isComingSoon` bayrağı var, modalın
 * məlumatı (treyler · təsvir · heyət · premyera) isə buradan gəlir.
 *
 * ⚠ TAPILMAYANDA `null` — kontent «Tezliklə» şərtinə düşmürsə server 404 verir və
 *   çağıran ADİ modala düşür (qırmızı xətt: ölü klik yoxdur).
 * ⚠ KEŞLİDİR: TV-də eyni kart bir sessiyada dəfələrlə açılır (fokus geri qayıdır),
 *   hər dəfə şəbəkəyə çıxmaq 10-foot interfeysdə gözlə görünən gecikmədir.
 * ⚠ `ES2015` HƏDDİ: `Map` var, amma obyekt lüğəti daha ucuzdur və `hasOwnProperty`
 *   yoxlaması olmadan işlədilmir — açar slug-dur, prototip adları ilə toqquşa bilər.
 */
/*
  ⚠⚠ AÇAR DİL + SLUG-DIR, TƏK SLUG DEYİL (2026-08-30). Əvvəl açar yalnız slug idi:
     istifadəçi «Tezliklə» kartını açır (ad, təsvir, heyət azərbaycanca keşlənir),
     sonra dili «Русский» edir və EYNİ kartı açanda keş HƏMİN AZƏRBAYCANCA cavabı
     qaytarırdı — şəbəkəyə ümumiyyətlə çıxılmırdı, yəni modal app yenidən
     başlayana qədər köhnə dildə qalırdı. Dil açarın bir hissəsi olanda hər dil
     öz sətrini alır və köhnə dilin cavabı da itmir (geri qayıdanda hazırdır).
  ⚠ AYIRICI «\n»: slug-da və dil kodunda ola bilməyən simvol — «az» + «-x» kimi
    dəyərlərin toqquşmasının qarşısını alır.
*/
const upcomingItemCache: { [key: string]: { item: UpcomingItem; at: number } } = Object.create(null)
// 2026-10-11: böyüklərdən uşaq profilinə keş sızırdı, premyera dəyişməsi isə görünmürdü.
const UPCOMING_TTL_MS = 300000

function upcomingCacheKey(slug: string): string {
  return readLanguage() + '\n' + (readStored('kb_tv_max_age') || '') + '\n' + slug
}

export function getUpcomingItem(slug: string): Promise<UpcomingItem | null> {
  return lookupUpcomingItem(slug).then((result) => result.item)
}

/** «Tezliklə» kartı sorğusunun nəticəsi: `notFound` yalnız server 404 verəndə `true`-dur. */
export interface UpcomingLookup {
  item: UpcomingItem | null
  notFound: boolean
}

/*
  2026-10-23: `getUpcomingItem`-in SƏBƏBLİ variantı (`lookupContent` ilə eyni forma).
    NƏ TAPILDI: brauzer film modalı «Tezliklə» kartında bu sorğunun `null` cavabını HƏR
    halda adi detala ötürürdü; taymautda da `content/{slug}` çağırılırdı, o isə plana
    salınmış (status=2) kontentdə 404 verir → «Bu məzmun açıla bilmədi».
    NİYƏ BELƏ: 404 = kontent artıq «Tezliklə» deyil (kart bayrağı köhnədir) — adi detal
    MƏNALIDIR; şəbəkə xətası/taymaut isə detal ucunun həll edə bilmədiyi haldır, modal
    «Tezliklə» rejimində qalmalıdır. Ayırd etmək üçün səbəb lazımdır.
*/
export function lookupUpcomingItem(slug: string): Promise<UpcomingLookup> {
  const key = upcomingCacheKey(slug)
  const cached = upcomingItemCache[key]
  if (cached && Date.now() - cached.at < UPCOMING_TTL_MS) return Promise.resolve({ item: cached.item, notFound: false })
  return withTimeout(
    httpJson<UpcomingItem>(withLanguage('/api/catalog/upcoming/' + encodeURIComponent(slug))),
    CLICK_TIMEOUT_MS,
    'catalog/upcoming-item',
  )
    .then((item): UpcomingLookup => {
      if (!item || !item.slug) return { item: null, notFound: false }
      if (!item.title) item.title = item.slug
      if (!item.ageLimit) item.ageLimit = null
      // Reytinq MƏCBURİ atılır — siyahı yolundakı eyni qayda (film çıxmayıb).
      item.rating = null
      upcomingItemCache[key] = { item, at: Date.now() }
      return { item, notFound: false }
    })
    // Şəbəkə xətası KEŞLƏNMİR — növbəti klik yenidən cəhd etsin.
    .catch((error: unknown): UpcomingLookup => ({ item: null, notFound: isHttpNotFound(error) }))
}

/** Kəşf filtri üçün janr — YALNIZ dərc olunmuş kontenti olanlar, say ilə. */
export interface Genre {
  slug: string
  name: string
  count: number
}

/** Kəşfin bir səhifəsi. `total` bütün filtr nəticəsidir, səhifənin yox. */
export interface BrowsePage {
  items: ContentCard[]
  total: number
}

/**
 * Janr çipləri (`/api/catalog/genres`).
 *
 * ⚠ ALINMASA BOŞ MASSİV — ekran çökmür, sadəcə janr sırası görünmür və növ
 * çipləri işləməyə davam edir. Janr siyahısı kəşfin İKİNCİ dərəcəli filtridir;
 * onun üstündən bütün ekranı «yüklənmədi» halına salmaq həddindən artıq olardı.
 */
export function getGenres(): Promise<Genre[]> {
  return withTimeout(httpJson<Genre[]>(withLanguage('/api/catalog/genres')), TIMEOUT_MS, 'catalog/genres')
    .then((list) => (list && list.length ? list : []))
    .catch(() => [])
}

/**
 * Kəşf — növ/janr filtri ilə səhifələnmiş siyahı (`/api/catalog/browse`).
 *
 * ⚠ FİLTR SERVER TƏRƏFDƏDİR (telefon və Apple TV ilə eyni qərar): bütün kataloqu
 * çəkib TV-nin yaddaşında süzmək olmaz — 2000 kartlıq siyahı köhnə TV-də yaddaşı
 * doldurur və şəkil növbəsini boğur.
 *
 * ⚠ XƏTADA `null` (boş siyahı DEYİL): ekran «nəticə yoxdur» ilə «yüklənmədi»
 * hallarını ayırd etməlidir. Boş massiv qaytarsaydıq şəbəkə qırıqlığı istifadəçiyə
 * «bu filtrdə film yoxdur» kimi görünərdi — yanlış məlumat, üstəlik «yenidən cəhd
 * et» təklifi də çıxmazdı.
 */
export function browseCatalog(
  kind: string | null,
  genre: string | null,
  page: number,
  pageSize: number,
): Promise<BrowsePage | null> {
  let path = '/api/catalog/browse?page=' + page + '&pageSize=' + pageSize
  if (kind) path += '&kind=' + encodeURIComponent(kind)
  if (genre) path += '&genre=' + encodeURIComponent(genre)
  return withTimeout(httpJson<BrowsePage>(withLanguage(path)), TIMEOUT_MS, 'catalog/browse')
    .then((raw) => (raw && raw.items ? { items: raw.items, total: raw.total || raw.items.length } : { items: [], total: 0 }))
    .catch(() => null)
}

/** Bir kolleksiyanın tam siyahısı — sıra başlığındakı «Hamısı ›» keçidinin hədəfi. */
export interface CollectionList {
  title: string
  items: ContentCard[]
}

/** Same collection rows as the website, including each row's preview cards. */
export interface CollectionRow extends CollectionList {
  slug: string
  count: number
}

export function getCollections(): Promise<CollectionRow[] | null> {
  return withTimeout(
    httpJson<CollectionRow[]>(withLanguage('/api/catalog/collections')),
    TIMEOUT_MS,
    'catalog/collections',
  ).then((raw) => {
    if (!Array.isArray(raw)) return null
    return raw.filter((row) => row && row.slug).map((row) => ({
      slug: row.slug,
      title: row.title || row.slug,
      count: row.count || 0,
      items: Array.isArray(row.items) ? row.items : [],
    }))
  }).catch(() => null)
}

/** Kəşf ekranının kolleksiya düymələri üçün — yalnız ad + slug. */
export interface CollectionLink {
  slug: string
  title: string
}

/**
 * Bütün aktiv kolleksiyalar — Kəşf ekranının BİRİNCİ çip sırası (sahib tələbi
 * 2026-08-11: «kəşfetdə film serial tamaşa konsert olan hissəni ləğv et, o
 * hissədə kolleksiyalar olsun»).
 *
 * ⚠ `itemsPerRow=1` — uc hər kolleksiya ilə birlikdə kartlar qaytarır, bizə isə
 *   yalnız ad lazımdır; parametr cavabı ~20 dəfə kiçildir (Android/Apple TV-dəki
 *   eyni qərar: `collections(itemsPerRow = 1)`).
 * ⚠ XƏTADA BOŞ MASSİV (null DEYİL): çip sırası sadəcə çəkilmir — ekranın qalanı
 *   (janr çipləri + şəbəkə) işləməyə davam edir, yəni dead-end yaranmır.
 */
export function getCollectionLinks(): Promise<CollectionLink[]> {
  return withTimeout(
    httpJson<Array<{ slug?: string; title?: string | null }>>(
      withLanguage('/api/catalog/collections') + '&itemsPerRow=1',
    ),
    TIMEOUT_MS,
    'catalog/collections',
  )
    .then((raw) => {
      if (!raw || !raw.length) return []
      const links: CollectionLink[] = []
      for (let i = 0; i < raw.length; i++) {
        const row = raw[i]
        if (!row || !row.slug) continue
        // Başlıq tərcüməsi yoxdursa slug göstərilir (Android VM qaydası).
        links.push({ slug: row.slug, title: row.title || row.slug })
      }
      return links
    })
    .catch(() => [])
}

/**
 * Kolleksiya siyahısı (`GET /api/catalog/collections/{slug}` — Android
 * `KinoboxClient.collection()` ilə eyni uc, «sıradakı „Hamısı" keçidinin hədəfi»).
 *
 * ⚠ XƏTADA `null` (boş siyahı DEYİL) — `browseCatalog` ilə eyni qərar: siyahı
 *   ekranı «bölmə boşdur» ilə «yüklənmədi» hallarını ayırd etməlidir.
 */
export function getCollection(slug: string): Promise<CollectionList | null> {
  return withTimeout(
    httpJson<{ title?: string | null; slug?: string; items?: ContentCard[] }>(
      withLanguage('/api/catalog/collections/' + encodeURIComponent(slug)),
    ),
    TIMEOUT_MS,
    'catalog/collection',
  )
    .then((raw) => {
      if (!raw) return null
      // Başlıq tərcüməsi yoxdursa slug göstərilir (Android VM qaydası).
      return { title: raw.title || raw.slug || slug, items: raw.items || [] }
    })
    .catch(() => null)
}

/**
 * «Ən çox baxılanlar» (`GET /api/catalog/popular`) — Android `KinoboxClient.popular()`
 * hansı ucu çağırırsa onun veb qarşılığı; hero-altı qısayol bu siyahını açır.
 *
 * ⚠ `pageSize=48` Android client-in defoltu ilə eynidir və SƏHİFƏLƏMƏ YOXDUR:
 *   Android `CatalogListViewModel.Kind.Popular` da siyahını bir sorğu ilə çəkir.
 * ⚠ XƏTADA `null` — yuxarıdakı eyni qərar (ekran «yüklənmədi» deyə bilsin).
 */
export function getPopular(): Promise<ContentCard[] | null> {
  return withTimeout(
    httpJson<BrowsePage>(withLanguage('/api/catalog/popular?page=1&pageSize=48')),
    TIMEOUT_MS,
    'catalog/popular',
  )
    .then((raw) => (raw && raw.items ? raw.items : []))
    .catch(() => null)
}

/**
 * Axtarış nəticəsindəki şəxs (aktyor · rejissor) — `SearchPersonDto` qarşılığı.
 * ⚠ `roleName` boş ola bilir (kataloqda rol yazılmayıb) — ekran onda «Şəxs» yazır.
 */
export interface SearchPerson {
  id: string
  slug: string
  name: string | null
  photoUrl: string | null
  roleName: string | null
}

/** Kataloq axtarışının TAM cavabı: filmlər və şəxslər ayrı qruplarda. */
export interface SearchResult {
  contents: ContentCard[]
  persons: SearchPerson[]
}

/** Şəxs səhifəsinin məlumatı (`GET /api/catalog/person/{slug}` → `PublicPersonDto`). */
export interface PersonDetail {
  id: string
  slug: string
  name: string | null
  biography: string | null
  photoUrl: string | null
  birthDate: string | null
  filmography: ContentCard[]
}

/** Axtarış sırasındakı şəxs sayının tavanı — səbəb aşağıdadır. */
const MAX_SEARCH_PERSONS = 12

/**
 * Açıq kataloq axtarışı — FİLMLƏR VƏ ŞƏXSLƏR (pultla yazılan sorğu).
 *
 * ⚠⚠ ŞƏXSLƏR ƏVVƏL ATILIRDI (2026-08-13-ə qədər): server `{contents, persons}`
 *    qaytarır, bu funksiya isə yalnız `contents`-i oxuyurdu — yəni aktyor adı
 *    yazan istifadəçi «nəticə tapılmadı» görürdü, halbuki Android TV həmin
 *    sorğuda şəxs sırasını göstərir (`TvSearchScreen` → `PersonCard`).
 * ⚠ ŞƏXS SAYI 12 İLƏ MƏHDUDDUR: sıra üfüqidir və pultla gəzilir; uzun siyahı
 *   filmlərə enən yolu uzadardı (Android-də də sıra praktiki olaraq qısadır).
 */
export function searchAll(query: string): Promise<SearchResult> {
  const trimmed = query.trim()
  if (trimmed.length < 2) return Promise.resolve({ contents: [], persons: [] })
  return withTimeout(
    httpJson<{ contents?: ContentCard[]; persons?: SearchPerson[] }>(
      withLanguage('/api/catalog/search?q=' + encodeURIComponent(trimmed)),
    ),
    TIMEOUT_MS,
    'catalog/search',
  )
    .then((raw) => {
      const result = {
        contents: raw && raw.contents ? raw.contents.slice(0, 24) : [],
        persons: raw && raw.persons ? raw.persons.slice(0, MAX_SEARCH_PERSONS) : [],
      }
      // Axtarış hadisəsi (BR 27.4) — yalnız yekun ifadə üçün (izah `lib/events.ts`).
      scheduleSearchEvent(trimmed, result.contents.length + result.persons.length)
      return result
    })
    // Uydurma nəticə yoxdur (28.3) — boş nəticə «tapılmadı» vəziyyətinə düşür.
    .catch(() => ({ contents: [] as ContentCard[], persons: [] as SearchPerson[] }))
}

/** Yalnız filmlər — köhnə çağırış yerləri (brauzer kataloqu) üçün. */
export function searchCatalog(query: string): Promise<ContentCard[]> {
  return searchAll(query).then((result) => result.contents)
}

/**
 * Şəxs səhifəsi — foto · bioqrafiya · filmoqrafiya (Android `TvPersonScreen`
 * hansı ucu çağırırsa onun eynisi).
 *
 * ⚠ XƏTADA `null`: ekran «yüklənmədi» deyib «Yenidən cəhd et» düyməsi göstərir
 *   (siyahı ekranındakı eyni qayda — qara ekran qadağandır).
 * ⚠ FİLMOQRAFİYA KART CAVABIDIR: meta (il · müddət · janr) onun içindədir,
 *   yəni kartlar əlavə sorğu atmır (MYTasks 512 müqaviləsi).
 */
export function getPerson(slug: string): Promise<PersonDetail | null> {
  if (!slug) return Promise.resolve(null)
  return withTimeout(
    httpJson<PersonDetail>(withLanguage('/api/catalog/person/' + encodeURIComponent(slug))),
    TIMEOUT_MS,
    'catalog/person',
  )
    .then((raw) => {
      if (!raw || !raw.slug) return null
      if (!raw.filmography) raw.filmography = []
      return raw
    })
    .catch(() => null)
}

/**
 * API cavabını müdafiəli oxuyur.
 *
 * ⚠ Bu forma bir dəfə artıq dəyişib (`{hero,blocks}` → `{heroes,blocks}`) və
 * köhnə binar hələ də köhnə formanı verə bilir (bax web-consumer-progress
 * qeydi). TV app-ı bunun üzündən çökməməlidir — hər iki forma qəbul edilir.
 */
function normalizeHome(raw: unknown): Home {
  const source = raw as { heroes?: HeroItem[]; hero?: HeroItem; blocks?: HomeBlock[] } | null
  if (!source) return EMPTY_HOME

  let heroes: HeroItem[] = []
  if (source.heroes && source.heroes.length > 0) heroes = source.heroes
  else if (source.hero) heroes = [source.hero]

  const blocks: HomeBlock[] = []
  const rawBlocks = source.blocks || []
  for (let i = 0; i < rawBlocks.length && blocks.length < MAX_ROWS; i++) {
    const block = rawBlocks[i]
    if (!block || !block.items || block.items.length === 0) continue
    blocks.push({
      id: block.id,
      slug: block.slug,
      title: block.title,
      showRank: block.showRank === true,
      wideItems: block.wideItems === true,
      items: block.items.slice(0, MAX_ITEMS_PER_ROW),
    })
  }

  if (heroes.length === 0 && blocks.length === 0) return EMPTY_HOME
  return { heroes: heroes.slice(0, MAX_HEROES), blocks, source: 'api' }
}

/** Kart fonu üçün sabit ton — poster yoxdursa ekran boz qalmasın. */
export function toneOf(text: string): number {
  let hash = 0
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) & 0xffff
  return hash % 6
}
